import React from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import type { MenuItem, Policy } from '../../types/navigation';
import ADMIN_MENU from '../../config/adminMenu';
import { usePermission } from '../../api/permissions/PermissionContext';

function evaluatePolicy(policy: Policy, perm: ReturnType<typeof usePermission>): boolean {
  switch (policy.type) {
    case 'can':
      return perm.can(policy.action, policy.resource, policy.context);
    case 'role':
      return perm.hasRole(policy.role);
    case 'attribute':
      return perm.hasAttribute(policy.key, policy.value);
    case 'custom':
      return policy.evaluate(perm.currentUser);
    default:
      return false;
  }
}

function isItemVisible(item: MenuItem, perm: ReturnType<typeof usePermission>): boolean {
  if (!item.policies || item.policies.length === 0) return true;
  return item.policies.some((p) => evaluatePolicy(p, perm));
}

function filterVisible(items: MenuItem[], perm: ReturnType<typeof usePermission>): MenuItem[] {
  return items
    .map((item) => {
      const visible = isItemVisible(item, perm);
      if (!visible) return null;
      const children = item.children ? filterVisible(item.children, perm) : undefined;
      return { ...item, children } as MenuItem;
    })
    .filter(Boolean) as MenuItem[];
}

const normalize = (p: string) => p.replace(/\/+$/, '');
const pathMatch = (base: string, current: string) => {
  const b = normalize(base);
  const c = normalize(current);
  return c === b || c.startsWith(b + '/');
};

const AdminMenu: React.FC<{ className?: string } & { mobile?: boolean }> = ({ className = '', mobile = false }) => {
  const { t } = useTranslation();
  const perm = usePermission();
  const items = React.useMemo(() => filterVisible(ADMIN_MENU, perm), [perm]);
  const [openItemId, setOpenItemId] = React.useState<string | null>(null);
  const { pathname } = useLocation();

  const isItemActive = React.useCallback(
    (item: MenuItem): boolean => {
      if (item.to && pathMatch(item.to, pathname)) return true;
      if (item.children && item.children.length > 0) {
        return item.children.some((child) => child.to && pathMatch(child.to!, pathname));
      }
      return false;
    },
    [pathname]
  );

  if (items.length === 0) return null;

  // Menú móvil: mantener comportamiento colapsable
  if (mobile) {
    const [isOpen, setIsOpen] = React.useState(false);
    return (
      <div className={className}>
        <button
          onClick={() => setIsOpen((v) => !v)}
          className="flex items-center justify-between w-full text-gray-700"
        >
          <span>{t('common.navigation.admin', { defaultValue: 'Administración' })}</span>
          <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="pl-4 mt-2 space-y-2"
            >
              {items.map((item) => (
                <div key={item.id}>
                  {item.to ? (
                    <NavLink
                      to={item.to}
                      className={({ isActive }) =>
                        `block transition-colors ${
                          isActive || isItemActive(item)
                            ? 'text-blue-700 font-semibold'
                            : 'text-gray-600 hover:text-blue-600'
                        }`
                      }
                      aria-current={isItemActive(item) ? 'page' : undefined}
                    >
                      {t(item.labelKey, { defaultValue: item.labelKey.split('.').pop() || item.labelKey })}
                    </NavLink>
                  ) : (
                    <div
                      className={`block ${isItemActive(item) ? 'text-blue-700 font-semibold' : 'text-gray-600'} `}
                    >
                      {t(item.labelKey, { defaultValue: item.labelKey.split('.').pop() || item.labelKey })}
                    </div>
                  )}
                  {item.children && item.children.length > 0 && (
                    <div className="pl-4 mt-1 space-y-1">
                      {item.children.map((child) => (
                        <NavLink
                          key={child.id}
                          to={child.to || '#'}
                          className={({ isActive }) =>
                            `block px-1 rounded ${
                              isActive ? 'text-blue-700 bg-blue-50' : 'text-gray-600 hover:text-blue-600'
                            }`
                          }
                          aria-current={child.to && pathMatch(child.to, pathname) ? 'page' : undefined}
                        >
                          {t(child.labelKey, { defaultValue: child.labelKey.split('.').pop() || child.labelKey })}
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  // Menú desktop: barra horizontal de ancho completo, dropdown solo para submenús
  return (
    <nav className={`w-full ${className}`}>
      <ul className="flex flex-wrap items-center gap-6">
        {items.map((item) => (
          <li key={item.id} className="relative">
            {item.to ? (
              <NavLink
                to={item.to}
                className={({ isActive }) =>
                  `transition-colors font-medium pb-0.5 border-b-2 ${
                    isActive || isItemActive(item)
                      ? 'text-blue-700 border-blue-600'
                      : 'text-gray-700 border-transparent hover:text-blue-600'
                  }`
                }
                aria-current={isItemActive(item) ? 'page' : undefined}
              >
                {t(item.labelKey, { defaultValue: item.labelKey.split('.').pop() || item.labelKey })}
              </NavLink>
            ) : (
              <button
                type="button"
                className={`transition-colors font-semibold flex items-center gap-1 pb-0.5 border-b-2 ${
                  isItemActive(item) ? 'text-blue-700 border-blue-600' : 'text-gray-700 border-transparent hover:text-blue-600'
                }`}
                onMouseEnter={() => setOpenItemId(item.id)}
                onMouseLeave={() => setOpenItemId((prev) => (prev === item.id ? null : prev))}
                onFocus={() => setOpenItemId(item.id)}
                onBlur={() => setOpenItemId(null)}
                aria-expanded={openItemId === item.id}
              >
                {t(item.labelKey, { defaultValue: item.labelKey.split('.').pop() || item.labelKey })}
                {item.children && item.children.length > 0 && (
                  <ChevronDown className="w-4 h-4" />
                )}
              </button>
            )}
            {item.children && item.children.length > 0 && (
              <div
                onMouseEnter={() => setOpenItemId(item.id)}
                onMouseLeave={() => setOpenItemId(null)}
                className="absolute left-0 mt-2"
              >
                <AnimatePresence>
                  {openItemId === item.id && (
                    <motion.div
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 8 }}
                      className="min-w-56 bg-white rounded-md shadow-lg py-2 z-50 border"
                    >
                      {item.children.map((child) => (
                        <NavLink
                          key={child.id}
                          to={child.to || '#'}
                          className={({ isActive }) =>
                            `block px-4 py-2 transition-colors ${
                              isActive ? 'text-blue-700 bg-blue-50' : 'text-gray-700 hover:bg-blue-50 hover:text-blue-600'
                            }`
                          }
                          aria-current={child.to && pathMatch(child.to, pathname) ? 'page' : undefined}
                        >
                          {t(child.labelKey, { defaultValue: child.labelKey.split('.').pop() || child.labelKey })}
                        </NavLink>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}
          </li>
        ))}
      </ul>
    </nav>
  );
};

export default AdminMenu;
